/**
 * A new Spending account on demand ("New Spending account").
 *
 * Single mode runs every action from one Spending sub. On request the user
 * gets a new one: the old sub stays (it may still hold positions) and every
 * new action uses the new sub. The new sub's salt is derived from the
 * account's scan key instead of random bytes, so any device holding the keys
 * finds the same current Spending sub from chain data alone:
 *
 *   salt(n) = HMAC-SHA256(scanKey, "redacted/spending/v1" ‖ uint64_be(n))
 *
 * for the n-th new Spending account (n = 1, 2, …). The sub address follows
 * from the salt (instantiate2), so a device predicts the addresses of
 * generations 1, 2, 3 … and checks which of them it owns. The salt is public
 * once used (it is part of the creation message), but without the scan key
 * nobody can connect one generation to another. Nothing is stored or synced:
 * the recovery backup already holds the keys.
 */
import { signers } from "rujira.js";
import { primarySubwallet, type SubwalletInfo } from "./rotation";

export const SPENDING_SALT_DOMAIN = "redacted/spending/v1";

const domain = new TextEncoder().encode(SPENDING_SALT_DOMAIN);
const BLOCK = 64;

/**
 * HMAC-SHA256 (RFC 2104) over the app's SHA-256 (noble, via rujira.js).
 * Synchronous: the Spending account is chosen while an account scan is
 * published, without another asynchronous step.
 */
export const hmacSha256 = (
  key: Uint8Array,
  message: Uint8Array
): Uint8Array => {
  const sha256 = signers.cosmos.crypto.sha256;
  const padded = new Uint8Array(BLOCK);
  padded.set(key.length > BLOCK ? sha256(key) : key);
  const inner = new Uint8Array(BLOCK + message.length);
  const outer = new Uint8Array(BLOCK + 32);
  for (let index = 0; index < BLOCK; index += 1) {
    inner[index] = padded[index] ^ 0x36;
    outer[index] = padded[index] ^ 0x5c;
  }
  inner.set(message, BLOCK);
  outer.set(sha256(inner), BLOCK);
  const mac = sha256(outer);
  // The padded key material never outlives the call.
  padded.fill(0);
  inner.fill(0);
  outer.fill(0);
  return mac;
};

/** Salt of the n-th new Spending account (32 bytes). */
export const spendingSalt = (
  scanKey: Uint8Array,
  generation: number
): Uint8Array => {
  if (!Number.isSafeInteger(generation) || generation < 1)
    throw new Error("Invalid Spending account generation");
  if (scanKey.length !== 32) throw new Error("Invalid scan key");
  const message = new Uint8Array(domain.length + 8);
  message.set(domain);
  new DataView(message.buffer).setBigUint64(domain.length, BigInt(generation));
  return hmacSha256(scanKey, message);
};

export type SpendingGeneration = {
  /** 1 for the first new Spending account, 2 for the next, … */
  generation: number;
  address: string;
  /** Addresses of the earlier generations, oldest first. */
  earlier: readonly string[];
};

/**
 * The newest new Spending account among the owned subs: generations 1, 2, …
 * in order, stopping at the first one whose predicted address is not owned.
 * Undefined when the account never created one (it keeps today's rule).
 */
export const findSpendingGeneration = (
  owned: readonly string[],
  predict: (generation: number) => string
): SpendingGeneration | undefined => {
  const addresses = new Set(owned);
  const found: string[] = [];
  // Every generation is one of the owned subs: there cannot be more.
  for (let generation = 1; generation <= addresses.size; generation += 1) {
    const address = predict(generation);
    // A repeated address would be a broken prediction: stop there.
    if (!addresses.has(address) || found.includes(address)) break;
    found.push(address);
  }
  if (found.length === 0) return undefined;
  return {
    generation: found.length,
    address: found[found.length - 1],
    earlier: found.slice(0, -1),
  };
};

/**
 * Earlier Spending accounts of an account that created new ones: the first
 * Spending sub (the most used one outside the generations) and every earlier
 * generation. Their open positions stay where they are.
 */
export const earlierSpendingAccounts = (
  subwallets: readonly SubwalletInfo[],
  found?: SpendingGeneration
): string[] => {
  if (!found) return [];
  const generations = new Set([...found.earlier, found.address]);
  const first = primarySubwallet(
    subwallets.filter((sub) => !generations.has(sub.address))
  );
  return [...(first ? [first] : []), ...found.earlier];
};

/** A new Spending account can be requested again this long after the last one settled. */
export const REGENERATE_COOLDOWN_MS = 60_000;

/**
 * One new Spending account a day per private account: the next one is
 * possible once the chain has moved on this many blocks (24 h at THORChain's
 * 6 s) from the block that created the current one. Chain data only, so every
 * device of the account applies the same limit. The relayer's per-address
 * registration cap backs it up for anyone who bypasses the app.
 */
export const REGENERATE_INTERVAL_BLOCKS = 14_400;

type ScannedNote = {
  blockHeight: number;
  note: { kind: string; subwallet?: string | null };
};

/**
 * Block that created a sub: the height of its first control note (the
 * creation writes it). Undefined when the scan holds none.
 */
export const subwalletCreatedAt = (
  notes: readonly ScannedNote[],
  address: string
): number | undefined => {
  let first: number | undefined;
  for (const entry of notes)
    if (
      entry.note.kind === "control" &&
      entry.note.subwallet === address &&
      Number.isSafeInteger(entry.blockHeight) &&
      entry.blockHeight > 0 &&
      (first === undefined || entry.blockHeight < first)
    )
      first = entry.blockHeight;
  return first;
};

/**
 * Height from which the next new Spending account is possible. Undefined
 * when there is no daily limit: the account never created one (the first
 * is free), or the scan does not show when the current one was created.
 */
export const nextRegenerateHeight = (
  spending: SpendingGeneration | undefined,
  notes: readonly ScannedNote[]
): number | undefined => {
  if (!spending) return undefined;
  const created = subwalletCreatedAt(notes, spending.address);
  return created === undefined
    ? undefined
    : created + REGENERATE_INTERVAL_BLOCKS;
};

export type RegenerateAvailability =
  | { allowed: true }
  | {
      allowed: false;
      reason: "unavailable" | "busy" | "running" | "daily" | "cooldown";
      /** Daily limit or cooldown: when the next request is accepted (ms epoch). */
      retryAt?: number;
    };

/**
 * App-side limit on new Spending accounts. The relayer cannot limit them per
 * account (by design it does not know who asks), so the app allows one at a
 * time, only while nothing else runs, one a day (see
 * REGENERATE_INTERVAL_BLOCKS) and, after a failed try, the next one a minute
 * after the previous one settled. A click loop cannot use up the relayer's
 * sponsored registrations.
 */
export const regenerateAvailability = (state: {
  now: number;
  /** Unlocked single-mode account, ready for actions. */
  ready: boolean;
  /** A private action runs or its receipt is still open. */
  busy: boolean;
  /** A new Spending account is being created. */
  running: boolean;
  /** When the previous request settled (ms epoch). */
  settledAt?: number;
  /** Daily limit: the latest height, the height it opens at, block time. */
  daily?: { height: number; nextHeight: number; blockTimeMs: number };
}): RegenerateAvailability => {
  if (state.running) return { allowed: false, reason: "running" };
  if (!state.ready) return { allowed: false, reason: "unavailable" };
  if (state.busy) return { allowed: false, reason: "busy" };
  if (state.daily && state.daily.height < state.daily.nextHeight)
    return {
      allowed: false,
      reason: "daily",
      retryAt:
        state.now +
        (state.daily.nextHeight - state.daily.height) * state.daily.blockTimeMs,
    };
  if (
    state.settledAt !== undefined &&
    state.now < state.settledAt + REGENERATE_COOLDOWN_MS
  )
    return {
      allowed: false,
      reason: "cooldown",
      retryAt: state.settledAt + REGENERATE_COOLDOWN_MS,
    };
  return { allowed: true };
};

/**
 * Random pause before a new Spending account is funded for the first time
 * (20–60 s after it was created): its creation and its first funding do not
 * land next to each other. Kept in memory only.
 */
export const firstFundingPauseMs = (random: () => number = Math.random) =>
  Math.round(20_000 + random() * 40_000);

/**
 * Random gap a first funding keeps after a return it mirrors (60–120 s),
 * drawn once per return.
 */
export const mirrorGapMs = (random: () => number = Math.random) =>
  Math.round(60_000 + random() * 60_000);

/** How long returns are remembered for the mirror rule. */
export const RECENT_RETURN_MS = 10 * 60_000;

/** A new Spending account that has not been funded yet. */
export type FundingHold = { subwallet: string; notBefore: number };

/** A return to the Reserve the app just sent (public amounts only). */
export type RecentReturn = {
  subwallet: string;
  denom: string;
  amount: bigint;
  at: number;
  gapMs: number;
};

/** An amount within 2 % of a returned amount mirrors it. */
export const mirrorsReturn = (amount: bigint, returned: bigint): boolean => {
  if (amount <= 0n || returned <= 0n) return false;
  const difference = amount > returned ? amount - returned : returned - amount;
  return difference * 10_000n <= returned * 200n;
};

/**
 * When the first funding of a new Spending account may go out (ms epoch), or
 * undefined when it need not wait: not before its random pause ends, and not
 * within the gap after a return from another own account of (almost) the
 * same asset amount. Public amounts that match right after each other link
 * the old and the new account; spacing them out keeps the switch quiet.
 */
export const firstFundingNotBefore = (params: {
  hold?: FundingHold;
  target: string;
  /** What moves from the Reserve into the target (public amounts). */
  legs: readonly { denom: string; amount: bigint }[];
  returns: readonly RecentReturn[];
  now: number;
}): number | undefined => {
  const { hold, target, legs, returns, now } = params;
  if (!hold || hold.subwallet !== target || legs.length === 0) return undefined;
  let until = hold.notBefore;
  for (const entry of returns)
    if (
      entry.subwallet !== target &&
      legs.some(
        (leg) =>
          leg.denom === entry.denom && mirrorsReturn(leg.amount, entry.amount)
      )
    )
      until = Math.max(until, entry.at + entry.gapMs);
  return until > now ? until : undefined;
};
